import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Badge, Modal, Pagination, ConfirmDialog, Money } from '../components/ui';
import { fmtDate, toInputDate } from '../utils';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['draft','sent','under_review','active','expired','cancelled'];
const PROC_TYPES = ['Task Order','RFP','RFQ','BPA'];
const CONTRACT_TYPES = ['FFP','T&M','Hybrid'];
const CLEARANCES = ['None','Public Trust','Secret','Top Secret','TS/SCI'];

export function ContractForm({ contract, onSave, onClose }) {
  const [form, setForm] = useState({ name:'',contract_number:'',po_number:'',procurement_type:'RFP',contract_type:'FFP',role:'Prime',prime_name:'',contract_vehicle:'',security_clearance:'None',base_year_tcv:'',option_year_count:0,funded_value:'',subcontract_number:'',estimated_gross_margin_pct:'',account_id:'',opportunity_id:'',status:'draft',value:'',start_date:'',end_date:'',description:'', ...contract });
  const [accounts, setAccounts] = useState([]); const [opps, setOpps] = useState([]);
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get('/accounts', { params:{ limit:200 } }).then(r => setAccounts(r.data.data));
    api.get('/opportunities', { params:{ limit:200 } }).then(r => setOpps(r.data.data));
  }, []);

  const handleSave = async () => {
    if (!form.name) return setError('Contract name required');
    setSaving(true);
    try {
      const r = contract?.id ? await api.put(`/contracts/${contract.id}`, form) : await api.post('/contracts', form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={contract?.id ? 'Edit Contract' : 'New Contract'} onClose={onClose} size="modal-lg">
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="section-divider">Basic Info</div>
        <div className="form-group"><label className="form-label">Contract Name *</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Contract Number</label><input className="form-input" value={form.contract_number} onChange={set('contract_number')} placeholder="Auto-generated if blank" /></div>
          <div className="form-group"><label className="form-label">PO #</label><input className="form-input" value={form.po_number} onChange={set('po_number')} /></div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Procurement Type</label>
            <select className="form-select" value={form.procurement_type} onChange={set('procurement_type')}>{PROC_TYPES.map(t => <option key={t}>{t}</option>)}</select>
          </div>
          <div className="form-group"><label className="form-label">Contract Type</label>
            <select className="form-select" value={form.contract_type} onChange={set('contract_type')}>{CONTRACT_TYPES.map(t => <option key={t}>{t}</option>)}</select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Role</label>
            <select className="form-select" value={form.role} onChange={set('role')}><option>Prime</option><option>Sub</option></select>
          </div>
          <div className="form-group"><label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={set('status')}>{STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}</select>
          </div>
        </div>
        {form.role === 'Sub' && <div className="form-group"><label className="form-label">Prime Name</label><input className="form-input" value={form.prime_name} onChange={set('prime_name')} /></div>}
        <div className="section-divider">Financials</div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Total Value ($)</label><input className="form-input" type="number" value={form.value} onChange={set('value')} /></div>
          <div className="form-group"><label className="form-label">Base Year TCV ($)</label><input className="form-input" type="number" value={form.base_year_tcv} onChange={set('base_year_tcv')} /></div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Funded Value ($)</label><input className="form-input" type="number" value={form.funded_value} onChange={set('funded_value')} /></div>
          <div className="form-group"><label className="form-label">Option Year Count</label><input className="form-input" type="number" min="0" value={form.option_year_count} onChange={set('option_year_count')} /></div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Est. Gross Margin %</label><input className="form-input" type="number" value={form.estimated_gross_margin_pct} onChange={set('estimated_gross_margin_pct')} /></div>
          <div className="form-group"><label className="form-label">Subcontract #</label><input className="form-input" value={form.subcontract_number} onChange={set('subcontract_number')} /></div>
        </div>
        <div className="section-divider">Period of Performance</div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Start Date</label><input className="form-input" type="date" value={toInputDate(form.start_date)} onChange={set('start_date')} /></div>
          <div className="form-group"><label className="form-label">End Date</label><input className="form-input" type="date" value={toInputDate(form.end_date)} onChange={set('end_date')} /></div>
        </div>
        <div className="section-divider">Details</div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Contract Vehicle</label><input className="form-input" value={form.contract_vehicle} onChange={set('contract_vehicle')} /></div>
          <div className="form-group"><label className="form-label">Security Clearance</label>
            <select className="form-select" value={form.security_clearance} onChange={set('security_clearance')}>{CLEARANCES.map(c => <option key={c}>{c}</option>)}</select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Account</label>
            <select className="form-select" value={form.account_id} onChange={set('account_id')}>
              <option value="">Select account...</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Linked Opportunity</label>
            <select className="form-select" value={form.opportunity_id} onChange={set('opportunity_id')}>
              <option value="">None</option>{opps.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" value={form.description} onChange={set('description')} rows={3} /></div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Contract'}</button>
      </div>
    </Modal>
  );
}

export default function Contracts() {
  const [contracts, setContracts] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(1);
  const [search, setSearch] = useState(''); const [status, setStatus] = useState('active');
  const [year, setYear] = useState('');
  const [showForm, setShowForm] = useState(false); const [editContract, setEditContract] = useState(null);
  const [archiveId, setArchiveId] = useState(null); const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/contracts', { params:{ search, status, year, page, limit:25 } }); setContracts(r.data.data); setTotal(r.data.total); }
    finally { setLoading(false); }
  }, [search, status, year, page]);

  useEffect(() => { load(); }, [load]);

  const expiringSoon = (end, s) => { if (!end || s !== 'active') return false; const d = (new Date(end)-new Date())/(1000*60*60*24); return d>=0 && d<=30; };

  return (
    <>
      <div className="page-header"><span className="page-title">Contracts</span>
        {can('edit') && <button className="btn btn-primary" onClick={() => { setEditContract(null); setShowForm(true); }}>+ New Contract</button>}
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body" style={{ paddingBottom:12 }}>
            <div className="toolbar">
              <input className="search-input" placeholder="Search contracts..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
              <select className="filter-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                <option value="">All Statuses</option>{STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
              <select className="filter-select" value={year} onChange={e => { setYear(e.target.value); setPage(1); }}>
                <option value="">All Years</option>
                {[2023,2024,2025,2026,2027,2028].map(y => <option key={y} value={String(y)}>{y}</option>)}
                {[2023,2024,2025,2026,2027,2028].map(y => <option key={y} value={String(y)}>{y}</option>)}
              </select>
              <span className="text-muted text-sm" style={{ marginLeft:'auto' }}>{total} contracts</span>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Number</th><th>Name</th><th>Account</th><th>Type</th><th>Role</th><th>Status</th><th>Value</th><th>End Date</th><th>Year</th><th>Mods</th><th></th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={10} style={{ textAlign:'center',padding:40,color:'var(--gray-400)' }}>Loading...</td></tr>}
                {!loading && contracts.length === 0 && <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon">📄</div><div className="empty-state-text">No contracts found</div></div></td></tr>}
                {contracts.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}>
                    <td style={{ fontFamily:'monospace',fontSize:11,color:'var(--gray-500)' }}>{c.contract_number}</td>
                    <td><span className="td-link">{c.name}</span></td>
                    <td>{c.account_name||'—'}</td>
                    <td style={{ fontSize:12 }}>{c.procurement_type} · {c.contract_type}</td>
                    <td><Badge status={c.role} /></td>
                    <td><div style={{ display:'flex',alignItems:'center',gap:5 }}><Badge status={c.status} />{expiringSoon(c.end_date,c.status) && <span title="Expiring soon" style={{ color:'var(--warning)' }}>⚠️</span>}</div></td>
                    <td><Money value={c.value} /></td>
                    <td style={{ color:expiringSoon(c.end_date,c.status)?'var(--warning)':'inherit',fontWeight:expiringSoon(c.end_date,c.status)?600:400 }}>{fmtDate(c.end_date)}</td><td style={{ fontSize:12,color:'var(--gray-500)' }}>{c.end_date?String(c.end_date).substring(0,4):'—'}</td>
                    <td>{c.mod_count>0?<span style={{ background:'var(--primary-light)',color:'var(--primary)',padding:'2px 8px',borderRadius:12,fontSize:11,fontWeight:600 }}>{c.mod_count}</span>:'—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {can('edit') && <div style={{ display:'flex',gap:4 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditContract(c); setShowForm(true); }}>✏️</button>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Archive" onClick={() => setArchiveId(c.id)}>📦</button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} limit={25} total={total} onPage={setPage} />
        </div>
      </div>
      {showForm && <ContractForm contract={editContract} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
      {archiveId && <ConfirmDialog message="Archive this contract? Data is preserved." confirmLabel="Archive" onConfirm={async () => { await api.delete(`/contracts/${archiveId}`); setArchiveId(null); load(); }} onCancel={() => setArchiveId(null)} />}
    </>
  );
}
